import { z } from 'zod'
import { amenitiesParamsSchema } from './data'
import { widgetSchema } from '../../common/schema'
import { AMENITY_ICON_IDS } from './iconResolver'
// An unrecognized stored value degrades to the default at parse time instead
// of throwing, so a hand-edited or pre-#1423 widget record never breaks the
// config form. Shared with the explorer and the card-skin leaves since #902.
import { lenientEnumDefault } from '../../common/zod-lenient'

export const amenitiesWidgetConfig = widgetSchema.extend({
  section: z.object({
    show: z.boolean().default(false).describe('Show the section title above the widget content'),
    title: z.string().nullish().default('Features and Amenities').describe('Section title text'),
  }).default({ show: false, title: 'Features and Amenities' }).describe('Section heading shown above the widget content'),
  filter: amenitiesParamsSchema,
  columns: z.object({
    sm: z.number().int().default(2).describe('Columns on small screens'),
    md: z.number().int().default(3).describe('Columns on medium screens (780px+)'),
    lg: z.number().int().default(4).describe('Columns on large screens (1200px+)'),
  }).default({
    sm: 2,
    md: 3,
    lg: 4,
  }).describe('Responsive grid columns'),
  countInExcerpt: z.number().int().min(1).default(12).describe('Number of amenities shown before "Show More"'),
  defaultIcon: z.string().default('icon-check').describe('Icomoon class name used when an amenity has no icon attached'),
  iconFallback: lenientEnumDefault(['check', 'none'], 'check')
    .describe('What to show when an amenity has no icon_class from Central, no keyword match, and no override: the fallback icon above, or nothing'),
  iconOverrides: z.record(z.string(), z.string()).default({})
    .describe(`Per-amenity icon override: amenity name (case-insensitive) to icon id, "check", or "none". Unrecognized values are ignored. Icon ids: ${AMENITY_ICON_IDS.join(', ')}`),
  translations: z.object({
    showMoreButton: z.record(z.string(), z.string()).default({
      en_US: 'Show More',
    }).describe('Show more button text for different locales'),
  }).default({
    showMoreButton: { en_US: 'Show More' },
  }).describe('Widget translations'),
  showMoreButton: z.boolean().default(true).describe('Whether to show the more button'),
})

export type AmenitiesWidgetConfig = z.infer<typeof amenitiesWidgetConfig>
